(function () {
  const mediaSelector = '.product-card-loading-media';

  function matchingElements(root, selector) {
    const elements = root.matches?.(selector) ? [root] : [];
    return elements.concat(Array.from(root.querySelectorAll?.(selector) || []));
  }

  function selectActiveImage(wrapper) {
    return wrapper.querySelector('.swiper-slide-active img') ||
      wrapper.querySelector('.product-main-image.is-active img') ||
      wrapper.querySelector('img:not([data-gallery-src])');
  }

  class LoadingAnimationVisibility {
    constructor({ selector = mediaSelector, createObserver = callback => typeof IntersectionObserver === 'undefined' ? null : new IntersectionObserver(callback) } = {}) {
      this.registered = new Set();
      this.selector = selector;
      this.observer = createObserver(entries => {
        entries.forEach(({ target, isIntersecting }) => {
          if (!this.registered.has(target)) return;
          target.setAttribute('data-loading-animation-visible', String(isIntersecting));
        });
      });
    }

    initialize(root) {
      matchingElements(root, this.selector).forEach(wrapper => {
        if (this.registered.has(wrapper)) return;
        this.registered.add(wrapper);
        wrapper.setAttribute('data-loading-animation-visible', String(!this.observer));
        this.observer?.observe(wrapper);
      });
    }

    release(root) {
      matchingElements(root, this.selector).forEach(wrapper => {
        if (!this.registered.has(wrapper)) return;
        this.observer?.unobserve(wrapper);
        this.registered.delete(wrapper);
        wrapper.removeAttribute('data-loading-animation-visible');
      });
    }
  }

  class DeferredImageLoader {
    constructor({ createResizeObserver = callback => typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(callback) } = {}) {
      this.originalSizes = new WeakMap();
      this.activeImages = new WeakSet();
      this.resizeObserver = createResizeObserver(entries => {
        entries.forEach(({ target }) => {
          if (this.activeImages.has(target)) this.updateSize(target);
        });
      });
    }

    updateSize(image) {
      const width = image.getBoundingClientRect().width;
      const fallback = (this.originalSizes.get(image) || '100vw').replace(/^auto,?\s*/, '') || '100vw';
      const sizes = width > 0 ? `${Math.ceil(width)}px` : fallback;
      if (image.sizes !== sizes) image.sizes = sizes;
    }

    load(image, { priority } = {}) {
      const deferred = image.hasAttribute('data-gallery-src');
      if (priority && (deferred || priority === 'high')) image.fetchPriority = priority;
      if (!deferred) return;
      this.originalSizes.set(image, image.sizes);
      this.activeImages.add(image);
      this.updateSize(image);
      image.loading = 'eager';
      ['srcset', 'src'].forEach(attribute => {
        const value = image.getAttribute(`data-gallery-${attribute}`);
        if (!value) return;
        image.setAttribute(attribute, value);
        image.removeAttribute(`data-gallery-${attribute}`);
      });
      this.resizeObserver?.observe(image);
    }

    release(root) {
      matchingElements(root, 'img').forEach(image => {
        this.activeImages.delete(image);
        this.resizeObserver?.unobserve(image);
      });
    }

    disconnect() {
      this.resizeObserver?.disconnect();
      this.activeImages = new WeakSet();
    }
  }

  class ProductCardImagePresenter {
    constructor({ decode = image => image.decode?.(), selectImage = selectActiveImage } = {}) {
      this.decode = decode;
      this.selectImage = selectImage;
      this.bindings = new WeakMap();
    }

    track(image) {
      const wrapper = image.closest(mediaSelector);
      if (!wrapper || this.bindings.get(wrapper)?.image === image) return;
      this.bindings.get(wrapper)?.abort.abort();
      const binding = { image, abort: new AbortController() };
      this.bindings.set(wrapper, binding);
      const { signal } = binding.abort;
      wrapper.dataset.productImageLoadingInitialized = 'true';
      wrapper.dataset.productImageLoading = '';
      wrapper.classList.add('is-image-loading');

      const reveal = async () => {
        try {
          if (image.naturalWidth > 0) await this.decode(image);
        } catch {}
        if (signal.aborted || this.bindings.get(wrapper) !== binding) return;
        image.classList.add('is-image-loaded');
        wrapper.classList.remove('is-image-loading');
        binding.abort.abort();
      };

      image.addEventListener('load', reveal, { once: true, signal });
      image.addEventListener('error', reveal, { once: true, signal });
      if (image.complete && image.hasAttribute('src') && !image.hasAttribute('data-gallery-src')) reveal();
    }

    initialize(root) {
      const wrappers = matchingElements(root, mediaSelector);
      const parent = root.closest?.(mediaSelector);
      if (parent && !wrappers.includes(parent)) wrappers.push(parent);
      wrappers.forEach(wrapper => {
        const image = this.selectImage(wrapper);
        if (image) this.track(image);
      });
    }

    release(root) {
      matchingElements(root, mediaSelector).forEach(wrapper => {
        this.bindings.get(wrapper)?.abort.abort();
        this.bindings.delete(wrapper);
      });
    }
  }

  class PageLoadReadiness {
    constructor({ page, events }) {
      this.page = page;
      this.events = events;
    }

    subscribe(ready) {
      if (this.page.readyState === 'complete') {
        ready();
        return () => {};
      }
      this.events.addEventListener('load', ready, { once: true });
      return () => this.events.removeEventListener('load', ready);
    }
  }

  class ImagePrefetchRequest {
    constructor({ loader }) {
      this.loader = loader;
    }

    load(image, { signal }) {
      if (!image || signal.aborted) return Promise.resolve();
      return new Promise(resolve => {
        const finish = () => {
          image.removeEventListener('load', finish);
          image.removeEventListener('error', finish);
          signal.removeEventListener('abort', finish);
          resolve();
        };
        image.addEventListener('load', finish, { once: true });
        image.addEventListener('error', finish, { once: true });
        signal.addEventListener('abort', finish, { once: true });
        this.loader.load(image, { priority: 'low' });
        if (!image.hasAttribute('src') || (image.complete && !image.hasAttribute('data-gallery-src'))) finish();
      });
    }
  }

  const productCardPrefetchPolicy = {
    images: card => Array.from(card.querySelectorAll('.swiper-slide img')).slice(0, 4),
    initial: images => images[1],
    afterSelection: (images, index) => index > 0 ? images.slice(2) : []
  };

  class ProductCardImagePrefetcher {
    constructor({ request, readiness, page, createObserver, policy = productCardPrefetchPolicy }) {
      this.request = request;
      this.policy = policy;
      this.cards = new Map();
      this.ready = false;
      this.page = page;
      this.observer = createObserver(entries => entries.forEach(({ target, isIntersecting }) => {
        const state = this.cards.get(target);
        if (!state) return;
        state.nearby = isIntersecting;
        this.prepareSecond(state);
      }));
      this.unsubscribeReady = readiness.subscribe(() => {
        this.ready = true;
        this.cards.forEach(state => this.prepareSecond(state));
      });
    }

    initialize(root) {
      matchingElements(root, '.product-card-swiper').forEach(card => {
        if (this.cards.has(card)) return;
        const images = this.policy.images(card);
        if (images.length < 2) return;
        const state = { images, nearby: !this.observer, prepared: false, started: false, abort: new AbortController() };
        this.cards.set(card, state);
        this.observer?.observe(card);
        this.prepareSecond(state);
      });
    }

    prepareSecond(state) {
      if (!this.ready || !state.nearby || state.prepared || state.abort.signal.aborted) return;
      state.prepared = true;
      this.request.load(this.policy.initial(state.images), { signal: state.abort.signal });
    }

    async select(card, index) {
      this.initialize(card);
      const state = this.cards.get(card);
      if (!state || state.started) return;
      const images = this.policy.afterSelection(state.images, index);
      if (!images.length) return;
      state.started = true;
      for (const image of images) {
        if (state.abort.signal.aborted) break;
        await this.request.load(image, { signal: state.abort.signal });
      }
    }

    release(root) {
      if (root === this.page) this.unsubscribeReady();
      matchingElements(root, '.product-card-swiper').forEach(card => {
        this.cards.get(card)?.abort.abort();
        this.cards.delete(card);
        this.observer?.unobserve(card);
      });
    }
  }

  class ProductImageController {
    constructor({ loader, presenter, prefetcher }) {
      this.loader = loader;
      this.presenter = presenter;
      this.prefetcher = prefetcher;
    }

    loadSlide(slide, { priority } = {}) {
      if (!slide) return;
      slide.querySelectorAll('img').forEach(image => {
        this.presenter.track(image);
        this.loader.load(image, { priority });
      });
    }

    prefetchCard(card, index) {
      return this.prefetcher?.select(card, index);
    }

    releaseCard(card) {
      this.prefetcher?.release(card);
    }

    initialize(root) {
      this.presenter.initialize(root);
    }

    release(root) {
      this.loader.release(root);
      this.presenter.release(root);
    }
  }

  class MediaLifecycle {
    constructor(participants) {
      this.participants = participants;
    }

    initialize(root) {
      this.participants.forEach(participant => participant.initialize(root));
    }

    release(root) {
      this.participants.forEach(participant => participant.release(root));
    }
  }

  function mountProductImageLoading({ root, controller, createMutationObserver = callback => new MutationObserver(callback) }) {
    const start = () => controller.initialize(root);
    const sectionLoaded = event => controller.initialize(event.target);
    const sectionUnloaded = event => controller.release(event.target);
    root.addEventListener('shopify:section:load', sectionLoaded);
    root.addEventListener('shopify:section:unload', sectionUnloaded);
    if (root.readyState === 'loading') root.addEventListener('DOMContentLoaded', start, { once: true });
    else start();
    const observer = createMutationObserver(mutations => {
      mutations.forEach(mutation => {
        mutation.removedNodes.forEach(node => {
          if (!node.isConnected) controller.release(node);
        });
        mutation.addedNodes.forEach(node => {
          if (node.isConnected) controller.initialize(node);
        });
      });
    });
    observer.observe(root.documentElement, { childList: true, subtree: true });
    return () => {
      observer.disconnect();
      root.removeEventListener('DOMContentLoaded', start);
      root.removeEventListener('shopify:section:load', sectionLoaded);
      root.removeEventListener('shopify:section:unload', sectionUnloaded);
      controller.release(root);
    };
  }

  const api = { LoadingAnimationVisibility, DeferredImageLoader, ProductCardImagePresenter, PageLoadReadiness, ImagePrefetchRequest, ProductCardImagePrefetcher, ProductImageController, MediaLifecycle, mountProductImageLoading, selectActiveImage };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  const loader = new DeferredImageLoader();
  const prefetcher = new ProductCardImagePrefetcher({
    page: document,
    readiness: new PageLoadReadiness({ page: document, events: window }),
    request: new ImagePrefetchRequest({ loader }),
    createObserver: callback => typeof IntersectionObserver === 'undefined' ? null : new IntersectionObserver(callback, { rootMargin: '300px' })
  });
  const controller = new ProductImageController({ loader, presenter: new ProductCardImagePresenter(), prefetcher });
  const lifecycle = new MediaLifecycle([
    new LoadingAnimationVisibility({ selector: '.product-card-loading-media, .cs-media-carousel__media' }),
    controller,
    prefetcher
  ]);
  window.HOMProductImages = controller;
  mountProductImageLoading({ root: document, controller: lifecycle });
})();
